import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Drawer, { Field } from "@/components/Drawer";
import { StatusBadge, poStatusMap } from "@/components/StatusBadge";
import { Plus, Trash2, Pencil, Truck, X } from "lucide-react";
import { fmtMoney, fmtDate } from "@/lib/format";
import { toast } from "sonner";

const vendorEmpty = { name: "", category: "panels", contact_person: "", email: "", phone: "", address: "", gstin: "", notes: "" };
const poEmpty = { vendor_id: "", project_id: "", po_number: "", items: [], status: "draft", order_date: "", expected_date: "", notes: "" };

export default function Vendors() {
  const [tab, setTab] = useState("vendors");
  const [vendors, setVendors] = useState([]);
  const [pos, setPos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vDraw, setVDraw] = useState(null);
  const [poDraw, setPoDraw] = useState(null);

  const load = async () => {
    const [v, p, pr] = await Promise.all([api.get("/vendors"), api.get("/purchase-orders"), api.get("/projects")]);
    setVendors(v.data); setPos(p.data); setProjects(pr.data);
  };
  useEffect(() => { load(); }, []);

  const saveVendor = async (data) => {
    const payload = { ...data, email: data.email || null };
    try {
      if (vDraw.mode === "edit") await api.patch(`/vendors/${vDraw.data.id}`, payload);
      else await api.post("/vendors", payload);
      toast.success("Saved");
      setVDraw(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const removeVendor = async (id) => {
    if (!window.confirm("Delete vendor?")) return;
    await api.delete(`/vendors/${id}`);
    load();
  };

  const savePO = async (data) => {
    const payload = {
      ...data,
      project_id: data.project_id || null,
      order_date: data.order_date || null,
      expected_date: data.expected_date || null,
      items: data.items.map((i) => ({ ...i, quantity: Number(i.quantity) || 0, unit_price: Number(i.unit_price) || 0 })),
    };
    try {
      if (poDraw.mode === "edit") await api.patch(`/purchase-orders/${poDraw.data.id}`, payload);
      else await api.post("/purchase-orders", payload);
      toast.success("Saved");
      setPoDraw(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };
  const removePO = async (id) => {
    if (!window.confirm("Delete PO?")) return;
    await api.delete(`/purchase-orders/${id}`);
    load();
  };

  return (
    <div data-testid="vendors-page">
      <PageHeader
        title="Vendors & Procurement"
        subtitle="Manage supplier network and purchase orders."
        actions={
          tab === "vendors" ? (
            <button className="btn-primary flex items-center gap-2" data-testid="add-vendor-btn"
              onClick={() => setVDraw({ mode: "create", data: vendorEmpty })}>
              <Plus className="w-4 h-4" /> New Vendor
            </button>
          ) : (
            <button className="btn-primary flex items-center gap-2" data-testid="add-po-btn"
              onClick={() => setPoDraw({ mode: "create", data: { ...poEmpty, items: [{ description: "", quantity: 1, unit_price: 0 }] } })}>
              <Plus className="w-4 h-4" /> New PO
            </button>
          )
        }
      />

      <div className="flex gap-1 mb-4 bg-white rounded-lg p-1 border border-slate-200 w-fit">
        {[
          ["vendors", `Vendors (${vendors.length})`],
          ["pos", `Purchase Orders (${pos.length})`],
        ].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`vendor-tab-${k}`}
            className={`px-4 py-2 text-sm rounded-md transition-colors ${tab === k ? "bg-[#0F3A66] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "vendors" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vendors.map((v) => (
            <div key={v.id} className="crm-card p-4" data-testid={`vendor-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display font-semibold text-slate-900">{v.name}</div>
                  <span className="badge bg-[#10B981]/10 text-[#059669] mt-1 capitalize">{v.category}</span>
                </div>
                <div className="flex gap-1">
                  <button className="btn-ghost !p-1.5" onClick={() => setVDraw({ mode: "edit", data: { ...vendorEmpty, ...v } })}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button className="btn-ghost !p-1.5 text-red-600" onClick={() => removeVendor(v.id)}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="mt-3 text-sm text-slate-600 space-y-1">
                {v.contact_person && <div>{v.contact_person}</div>}
                {v.email && <div>{v.email}</div>}
                {v.phone && <div>{v.phone}</div>}
                {v.gstin && <div className="text-xs font-mono text-slate-500">GSTIN: {v.gstin}</div>}
              </div>
            </div>
          ))}
          {vendors.length === 0 && (
            <div className="col-span-full crm-card p-10 text-center text-slate-500">
              <Truck className="w-6 h-6 mx-auto mb-2 opacity-50" />
              No vendors yet.
            </div>
          )}
        </div>
      ) : (
        <div className="crm-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <Th>PO #</Th><Th>Vendor</Th><Th>Project</Th><Th>Status</Th>
                <Th className="text-right">Total</Th><Th>Ordered</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {pos.map((p) => (
                <tr key={p.id} className="border-t border-slate-200 hover:bg-slate-50/50" data-testid={`po-row-${p.id}`}>
                  <Td className="font-mono">{p.po_number || p.id.slice(0, 8)}</Td>
                  <Td>{vendors.find((v) => v.id === p.vendor_id)?.name || "—"}</Td>
                  <Td>{projects.find((pr) => pr.id === p.project_id)?.name || "—"}</Td>
                  <Td><StatusBadge value={p.status} map={poStatusMap} /></Td>
                  <Td className="text-right font-mono">{fmtMoney(p.totals?.total)}</Td>
                  <Td className="text-xs text-slate-500">{fmtDate(p.order_date)}</Td>
                  <Td className="text-right">
                    <button className="btn-ghost !p-1.5" onClick={() => setPoDraw({ mode: "edit", data: { ...poEmpty, ...p } })}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button className="btn-ghost !p-1.5 text-red-600" onClick={() => removePO(p.id)}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Td>
                </tr>
              ))}
              {pos.length === 0 && (
                <tr><td colSpan={7} className="p-10 text-center text-slate-500">No purchase orders.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {vDraw && (
        <Drawer open onClose={() => setVDraw(null)} title={vDraw.mode === "edit" ? "Edit Vendor" : "New Vendor"} testid="vendor-drawer"
          footer={
            <>
              <button className="btn-outline" onClick={() => setVDraw(null)}>Cancel</button>
              <button className="btn-primary" data-testid="vendor-save-btn" onClick={() => saveVendor(vDraw.data)}>Save</button>
            </>
          }
        >
          <VendorForm data={vDraw.data} onChange={(d) => setVDraw({ ...vDraw, data: d })} />
        </Drawer>
      )}

      {poDraw && (
        <Drawer open onClose={() => setPoDraw(null)} title={poDraw.mode === "edit" ? "Edit PO" : "New PO"} wide testid="po-drawer"
          footer={
            <>
              <button className="btn-outline" onClick={() => setPoDraw(null)}>Cancel</button>
              <button className="btn-primary" data-testid="po-save-btn" onClick={() => savePO(poDraw.data)}>Save</button>
            </>
          }
        >
          <POForm data={poDraw.data} onChange={(d) => setPoDraw({ ...poDraw, data: d })} vendors={vendors} projects={projects} />
        </Drawer>
      )}
    </div>
  );
}

function VendorForm({ data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <Field label="Name *"><input className="input-base" value={data.name} data-testid="vendor-name" onChange={(e) => set("name", e.target.value)} /></Field>
        <Field label="Category">
          <select className="input-base" value={data.category} onChange={(e) => set("category", e.target.value)}>
            {["panels", "inverters", "structure", "cables", "services", "other"].map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
        <Field label="Contact Person"><input className="input-base" value={data.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} /></Field>
        <Field label="Email"><input className="input-base" type="email" value={data.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Phone"><input className="input-base" value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
        <Field label="GSTIN"><input className="input-base" value={data.gstin || ""} onChange={(e) => set("gstin", e.target.value)} /></Field>
      </div>
      <Field label="Address"><textarea className="input-base min-h-[60px]" value={data.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
      <Field label="Notes"><textarea className="input-base min-h-[60px]" value={data.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
    </>
  );
}

function POForm({ data, onChange, vendors, projects }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  const items = data.items || [];
  const setItem = (i, k, v) => set("items", items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addItem = () => set("items", [...items, { description: "", quantity: 1, unit_price: 0 }]);
  const removeItem = (i) => set("items", items.filter((_, idx) => idx !== i));
  const total = items.reduce((a, i) => a + (Number(i.quantity) || 0) * (Number(i.unit_price) || 0), 0);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <Field label="Vendor *">
          <select className="input-base" value={data.vendor_id} data-testid="po-vendor" onChange={(e) => set("vendor_id", e.target.value)}>
            <option value="">— Select —</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </Field>
        <Field label="Project">
          <select className="input-base" value={data.project_id || ""} onChange={(e) => set("project_id", e.target.value)}>
            <option value="">—</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="PO Number"><input className="input-base" value={data.po_number || ""} onChange={(e) => set("po_number", e.target.value)} /></Field>
        <Field label="Status">
          <select className="input-base" value={data.status} onChange={(e) => set("status", e.target.value)}>
            {Object.entries(poStatusMap).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </Field>
        <Field label="Order Date"><input type="date" className="input-base" value={(data.order_date || "").slice(0, 10)} onChange={(e) => set("order_date", e.target.value)} /></Field>
        <Field label="Expected Date"><input type="date" className="input-base" value={(data.expected_date || "").slice(0, 10)} onChange={(e) => set("expected_date", e.target.value)} /></Field>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Items</div>
          <button className="btn-ghost text-sm" onClick={addItem} data-testid="po-add-item"><Plus className="w-3.5 h-3.5 inline" /> Add item</button>
        </div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <input className="input-base col-span-6" placeholder="Description" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} />
              <input className="input-base col-span-2" type="number" placeholder="Qty" value={it.quantity} onChange={(e) => setItem(i, "quantity", e.target.value)} />
              <input className="input-base col-span-3" type="number" placeholder="Unit Price" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} />
              <button className="btn-ghost !p-1.5 text-red-600 col-span-1" onClick={() => removeItem(i)}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="text-right mt-3 font-mono text-sm text-slate-700">Total: {fmtMoney(total)}</div>
      </div>
      <Field label="Notes"><textarea className="input-base min-h-[60px]" value={data.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
    </>
  );
}

const Th = ({ children, className = "" }) => <th className={`px-4 py-3 text-left font-medium ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>;
